import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertBar, ButtonWithLoading, Content, PageHeader, Spinner } from 'scorer-ui-kit';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import { PageHeaderContainer, Table, TR, TH, TD, DeviceNameLabel, SpinnerDesign, AlertContainer, NoteLabel, CenterAlignedContainer } from '../Style';
import { getCameras, analyze } from '../apis';
import AutoRefresh from '../components/AutoRefresh';
import { useDevice } from '../hooks/useDevice';
import { StatusTranslate, ColorsTranslate, Camera, ANALYSIS_INTERVAL, ROI } from '../constants';
import { useLocation } from 'react-router-dom';

const AnalysisButton = styled(ButtonWithLoading)`
  width: 180px;
`;

const RightAlignedContainer = styled.div`
  float:right;
  display:flex;
  margin-bottom:10px;
`

const LEDTooltipTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  & > tr > td {
    padding: 2px 5px;
    vertical-align: top;
  }
  & > tr > td:first-child {
    text-align: left;
  }
  & > tr > td:last-child {
    text-align: left;
  }
`;

function LedStatus() {
  const [analysisStatus, setAnalysisStatus] = useState<any>();
  const { state: { device, status_code, deviceLoading }, actions: { fetchDevice } } = useDevice();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [camerasLoading, setCamerasLoading] = useState<boolean>(true);
  const [analysisLoading, setAnalysisLoading] = useState<boolean>(true);
  const [isDevice, setIsDevice] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isPrevCallCompleted, setIsPrevCallCompleted] = useState(false);
  const [analysisAlert, setAnalysisAlert] = useState<{ message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ message: '', type: undefined });
  const { t } = useTranslation(['LedStatus', 'DetectionSettings']);
  const location = useLocation();

  const isPrevCallCompletedRef = useRef(isPrevCallCompleted);
  isPrevCallCompletedRef.current = isPrevCallCompleted;

  const getAllCameras = useCallback(() => {
    getCameras()
      .then((res) => {
        if (res.data.status_code === '0') {
          setCameras(res.data.data.cameras);
        } else {
          setMessage(res.data.message);
        }
        setCamerasLoading(false);
      })
      .catch((err) => {
        if (err.response.data.message && !isDevice) {
          setMessage(err.response.data.message);
          setCamerasLoading(false);
        }
      });
  }, [isDevice]);

  useEffect(() => {
    if (!deviceLoading) {
      if (status_code === '0') {
        setIsDevice(true);
      } else if (status_code === '2') {
        setIsDevice(false);
        setMessage(t('Common:deviceNotFound'));
        setCamerasLoading(false);
      }
    }
  }, [device, status_code, deviceLoading, t]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  useEffect(() => {
    sessionStorage.removeItem('cameraTab');
    sessionStorage.removeItem('notificatioinTab');
    sessionStorage.removeItem('auditLogTab');
  }, [location]);

  const autoRefreshCallback = useCallback(() => {
    getAllCameras();
  }, [getAllCameras]);

  useEffect(() => {
    autoRefreshCallback();
  }, [autoRefreshCallback]);

  const ChangeAnalysisStatus = () => {
    let jsonData;
    let action: string;
    if (analysisStatus !== 'stop') {
      action = 'stop';
      jsonData = { action };
    } else {
      action = 'start';
      jsonData = { action };
    }

    analyze(jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          setAnalysisStatus(action === 'start' ? 'run' : 'stop');
        } else if (res.data.status_code === '101') {
          setAnalysisAlert({ message: t('DetectionSettings:snapshotReloadIsRunning'), type: 'info' });
        } else if (res.data.status_code === '102') {
          setAnalysisAlert({ message: t('DetectionSettings:referenceStatusCheckIsAlreadyRunning'), type: 'info' });
        } else if (res.data.status_code === '103') {
          setAnalysisAlert({ message: t('DetectionSettings:cameraSettingsChangeIsRunning'), type: 'info' });
        } else if (res.data.status_code === '104') {
          setAnalysisStatus('run');
          setAnalysisAlert({ message: t('DetectionSettings:ledAnalysisIsRunning'), type: 'info' });
        } else if (res.data.status_code === '105') {
          setAnalysisAlert({ message: t('ledAnalysisIsAlreadyStopped'), type: 'info' });
          setAnalysisStatus('stop');
        } else if (res.data.status_code === '300') {
          console.error(t('timeoutError'));
        } else {
          setAnalysisAlert({ message: res.data.message, type: 'error' });
        }
        setAnalysisLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err.response.data.message) {
          setAnalysisAlert({ message: err.response.data.message, type: 'error' });
        }
      });
  };

  const isStatusValid = (desired_status: string, analyzed_status: string) => {
    if (desired_status === 'None' && (analyzed_status === 'On' || analyzed_status === 'Long Flash' || analyzed_status === 'Short Flash' || analyzed_status === 'Off')) {
      return true;
    } else {
      if (desired_status === analyzed_status) {
        return true;
      } else if (desired_status === 'Not On' && analyzed_status !== 'On') {
        return true;
      } else if (desired_status === 'Not Off' && analyzed_status !== 'Off') {
        return true;
      } else if (desired_status === 'Flash' && (analyzed_status === 'Long Flash' || analyzed_status === 'Short Flash')) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isColorValid = (desired_color: string[], analyzed_color: string) => {
    if (desired_color[0] === 'None' && (analyzed_color === 'red' || analyzed_color === 'orange' || analyzed_color === 'green' || analyzed_color === 'blue' || analyzed_color === 'user_specified')) {
      return true;
    } else {
      if (desired_color.includes(analyzed_color)) {
        return true;
      } else {
        return false;
      }
    }
  };
  // eslint-disable-next-line 
  const getStatusAndColor = (enabled: boolean, desired_status: string, desired_color: string[], analyzed_status: string, analyzed_color: string, connected: boolean) => {

    let showColorLabel, colorValue;
    if (!enabled || (analyzed_status === 'Not Available' || analyzed_status === null || analyzed_status === undefined || analyzed_status === 'None')) {
      showColorLabel = false;
      return { showColorLabel, colorValue: '' };
    } else if (!enabled || analyzed_status === 'Not configured') {
      showColorLabel = false;
      return { showColorLabel, colorValue: '', bgColor: '#e8e8e8' };
    } else if (analyzed_status === 'Off') {
      colorValue = isStatusValid(desired_status, analyzed_status) ? '' : 'red';
      showColorLabel = true;
      return { showColorLabel, colorValue };
    } else {
      colorValue = isColorValid(desired_color, analyzed_color) && isStatusValid(desired_status, analyzed_status) ? '' : 'red';
      showColorLabel = true;
      return { showColorLabel, colorValue };
    }
  };

  const getStatus = useCallback(() => {
    const jsonData = { action: 'get_status' };
    setIsPrevCallCompleted(false);
    analyze(jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          if (res.data.status !== null) {
            setAnalysisStatus(res.data.status);
          }
        } else if (res.data.status_code === '300') {
          console.error(t('timeoutError'));
          setAnalysisStatus('stop');
        } else {
          setAnalysisAlert({ message: res.data.message, type: 'error' });
        }
        setAnalysisLoading(false);
        setIsPrevCallCompleted(true);
      })
      .catch((err) => {
        console.error(err);
        if (err.response.data.message) {
          setAnalysisAlert({ message: err.response.data.message, type: 'error' });
        }
        setIsPrevCallCompleted(true);
      });
  }, [t]);

  const sortColors = useCallback((colors: string[])=>{
    let sortOrder = ['red', 'orange', 'green', 'blue', 'user_specified'];
    var ordering: { [key: string]: any } = {};

    for (var i=0; i<sortOrder.length; i++) {
        ordering[sortOrder[i]] = i;
    }

    colors.sort( function(a, b) {
        return (ordering[a] - ordering[b]) || a.localeCompare(b);
    });

    return colors;
  }, []);

  useEffect(() => {
    getStatus();

    const interval = setInterval(() => {
      if (isPrevCallCompletedRef.current) {
        getStatus();
      }
    }, ANALYSIS_INTERVAL);

    return (() => {
      clearInterval(interval);
    });
  }, [getStatus]);

  const getROIDetails = (rois: any) => {
    if (rois.length < 10) {
      let roisCount = rois.length;
      let roisDiff = 10 - roisCount
      for (var i = 0; i < roisDiff; i++) {
        const notConfigureROI = {} as ROI;
        notConfigureROI.analyzed_status = "Not configured"
        notConfigureROI.enabled = true
        rois.push(notConfigureROI);
      }
    }
  }

  const getRange = useCallback((color:string)=>{
    let range;
    switch (color) {
      case 'red':
        range = '(0-7 , 150 - 179)';
        break;
    
      case 'orange':
        range = '(8-29)';
        break;
    
      case 'green':
        range = '(30-89)';
        break;
    
      case 'blue':
        range = '(90-149)';
        break;
    
      default:
        break;
    }
    return range;
  },[]);

  return (
    <Content>
      <PageHeaderContainer>
        <PageHeader title={t('ledStatus')} icon='DevicesScorerEdge' areaTitle={t('result')} />
        <DeviceNameLabel htmlFor='' labelText={device.name} />
        <NoteLabel htmlFor='' labelText={device.detail} />
      </PageHeaderContainer>
      {
        camerasLoading ?
          <CenterAlignedContainer>
            <Spinner size='large' styling={SpinnerDesign} /> 
          </CenterAlignedContainer> :
          (message !== '' ? <AlertBar type='info' message={message} /> : null)
      }
      {!camerasLoading && isDevice && cameras.length > 0 &&
        <>
          <RightAlignedContainer>
            <AutoRefresh autoRefresh={autoRefreshCallback} pageName='led_status' />
            <AnalysisButton loading={analysisLoading} name='analysis' onClick={ChangeAnalysisStatus} design={analysisLoading ? 'primary' : (analysisStatus === 'stop') ? 'primary' : 'danger'}>{!analysisLoading && (analysisStatus === 'stop' ? t('startAnalysis') : t('stopAnalysis'))}</AnalysisButton>
          </RightAlignedContainer>
          {analysisAlert.message !== '' &&
            <AlertContainer>
              <AlertBar message={analysisAlert.message} type={analysisAlert.type} />
            </AlertContainer>}
          <Table style={{ marginRight: window.innerWidth < 1200 ? '-84px' : '', marginLeft: window.innerWidth < 1200 ? '-84px' : '' }}>
            <tbody>
              <TR>
                <TH bgColor='#f5f5f0' />
                {
                  Array.from(Array(10), (e, i) => {
                    return <TH bgColor='#f5f5f0'>ROI{i + 1}</TH>;
                  })
                }
              </TR>
              {
                cameras.map(({ name, rois, connected }) => {
                  const cameraName = name;
                  //rois=rois.slice(0,8)
                  return (
                    <TR key={cameraName}>
                      <TD bgColor='#f5f5f0' fontWeight='bold'>{cameraName}</TD>
                      {
                        rois.length <= 0 &&
                        <TD colSpan={10}>{t('noROIAvailable')}</TD>
                      }
                      {
                        rois.length < 10 && getROIDetails(rois)
                      }
                      {
                        connected ? rois.map(({ name, id, desired_status, analyzed_status, note, note2, desired_color, desired_hue_x, desired_hue_y, enabled, analyzed_color, analyzed_hue, consecutive_ok_count, consecutive_ng_count }) => {
                          const { showColorLabel, colorValue, bgColor } = getStatusAndColor(enabled, desired_status, desired_color, analyzed_status, analyzed_color, connected);
                          const sortedColors = sortColors(desired_color);
                          return <TD key={id} data-tip data-for={cameraName + id} color={colorValue} bgColor={enabled ? bgColor : '#e8e8e8'}>
                            {
                              enabled ?
                                t('Common:status.' + StatusTranslate[analyzed_status])
                                :
                                <span>{t('Common:status.disabled')}</span>
                            }

                            {
                              showColorLabel &&
                              <TD rowSpan={3} style={{ border: 'none', display: 'flex', justifyContent: 'center' }} color={colorValue}>
                                {
                                  analyzed_color!=='None' &&
                                  t('Common:color.' + ColorsTranslate[analyzed_color])
                                }
                                {analyzed_color!=='None' && ` (${ analyzed_hue})`}
                              </TD>
                            }
                            {analyzed_status !== 'Not configured' ? <ReactTooltip id={cameraName + id}>
                              <LEDTooltipTable>
                                <tr>
                                  <td>{device.roi_lable_name}</td>
                                  <td>:</td>
                                  <td>{name}</td>
                                </tr>
                                <tr>
                                  <td>{device.roi_lable_note1}</td>
                                  <td>:</td>
                                  <td>{note}</td>
                                </tr>
                                <tr>
                                  <td>{device.roi_lable_note2}</td>
                                  <td>:</td>
                                  <td>{note2}</td>
                                </tr>
                                <tr>
                                  <td>{'OK ' + t('Count')}</td>
                                  <td>:</td>
                                  <td>{`${consecutive_ok_count + ' / ' + device.ng_to_ok_threshold }`}</td>
                                </tr>
                                <tr>
                                  <td>{'NG ' + t('Count')}</td>
                                  <td>:</td>
                                  <td>{`${consecutive_ng_count + ' / ' + device.ok_to_ng_threshold }`}</td>
                                </tr>
                                <tr>
                                  <td>{t('desiredStatus')}</td>
                                  <td>:</td>
                                  <td>{t('Common:status.' + StatusTranslate[desired_status])}</td>
                                </tr>
                                <tr>
                                  <td>{
                                    desired_color.length===1 ? t('desiredColor') : t('desiredColors')
                                  }</td>
                                  <td>:</td>
                                  <td>
                                    <div>
                                      {sortedColors.map((item,index) =>
                                        <p key={`desired_color${item}${index}`} style={{margin: '0'}}>
                                          {
                                            item==='None'?
                                            `${t('Common:color.' + ColorsTranslate[item])}`
                                            : `${t('Common:color.' + ColorsTranslate[item])} ${item==='user_specified'? `(${desired_hue_x}-${desired_hue_y})`: getRange(item)}`
                                          }
                                        </p>)
                                      }
                                    </div>
                                  </td>
                                </tr>
                              </LEDTooltipTable>
                            </ReactTooltip> : null}
                          </TD>;

                        }) : <TD colSpan={10} bgColor='#e8e8e8'>{t('Common:status.cameraNotConnected')}</TD>
                      }

                    </TR>
                  );
                }
                )
              }
            </tbody>
          </Table>
        </>}
    </Content>
  );
}
export default LedStatus;
